/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react';
import {MediumText, UNSELECTABLE_TEXT} from '../../styles/styles';
import {BLACK, DARK_GREY, WHITE, YELLOW} from '../../styles/colors';
import {ERROR_BORDER} from '../../forms/styles';
import {FADE_IN_ANIMATION} from '../../styles/animations';
import {UserAr} from './userAr';
import {PropValidator} from '../../forms/validation/propValidation';
import {CRUD_STATE} from '../../forms/elements/crudButtons/crudState';
import {useCallback, useEffect, useMemo, useState} from 'react';
import { equals, find, path} from 'ramda';
import {PropValidationMsg} from '../../forms/validation/propValidationMsg';
import * as React from 'react';
import {doNothing, isNilOrEmpty} from '../../language/module';
import TooltipView from '../../layout/tooltip/TooltipView';
import {Input} from '../../forms/elements/Input';
import {Dropdown} from '../../forms/elements/select/Dropdown';
import Switcher from '../../layout/Switcher';
import {BigNeutral} from '../../forms/elements/buttons';
import PasswordField from '../../forms/elements/PasswordField';
import CrudButtons from '../../forms/elements/crudButtons/CrudButtons';
import Select from 'react-select';
import {
  checkIfUserChanged,
  crudStateFor,
  generateOptionsFromIds,
  generateResortOptions,
  hasAllResorts,
  mapIdsToResortAbbrevs,
  mapIdsToResorts,
} from './module';
import {Resort} from '../../resortDetails/resort';
import {useSelector} from 'react-redux';
import {IAppState} from '../../core/redux/IAppState';
import {useDebounce} from '../../language/hooks/useDebounce';
import {TooltipFC} from '../../layout/TooltipFC';
import {apiGetSuggestedUsername, checkUserNameAvailable, checkEmailAvailable} from '../../core/redux/api/endpoints';
import { EMAIL_REGEX, removeBlacklistedCharacters} from './validation';
import { convertUtcStringDateToDate, formatDate, UNIX_EPOCH } from '../../language/time';
import { getResource } from '../../language/resources/getResource';
import { MAX_FIRST_NAME_LENGTH, MAX_LAST_NAME_LENGTH, MAX_USERNAME_LENGTH } from './constants';
import expand from '../../assets/icons/circlePlus.svg';
import collapse from '../../assets/icons/circleMinus.svg';
import { useThrottleState } from '../../language/hooks/useThrottleState';

const ROW_ITEM_HEIGHT    = 40;

const styles = {
  rowItemHeight:         css`
    height: ${ROW_ITEM_HEIGHT}px;
  `,
  error:                 css`
    ${MediumText};
    background-color: ${BLACK};
    color: ${WHITE};
    padding: 10px;
    white-space: pre-line;
  `,
  errorBorder:           css`
    ${ERROR_BORDER};
    box-sizing: border-box;
  `,
  row:                   css`
    td {
      vertical-align: middle !important;
      ${MediumText};
      ${FADE_IN_ANIMATION};
    }

    td > * {
      ${FADE_IN_ANIMATION};
    }
  `,
  resetPasswordCheckbox: css`
    margin-left: 40px;
  `,
  passwordButton: css`
    height: ${ROW_ITEM_HEIGHT}px;
    padding: 5px 32px;
    width: 100%;
  `,
  email: css`
    min-width: 275px;
    word-break: break-all;
  `,
  userResortsOverview: css`
    display: flex; 
    flex-direction: row; 
    align-items: center;
  `,
  userResortListOverflow: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow:ellipsis;
    display: block;
  `,
  userResortsExpandButton: css`
    opacity: 75%; 
    border: none;
    background-color: transparent;
    cursor: pointer; 
    &:hover {
      opacity: 100%;
    }
  `,
  userResortsListContainer: css`
    width: 100%; 
    background-color: transparent; 
    padding: 5px; 
    display: flex; 
    flex-wrap: wrap;
  `,
  expandButton: css`
    margin-left: -5px;
    margin-right: 5px;
    width: 17px;
    height: 17px;
    ${UNSELECTABLE_TEXT};
  `,
};

const userResortsAbbreviations = css`
  width: 225px;
  flex-grow: 1;
  margin-right: 10px;
  ${styles.userResortListOverflow}
`;

type UserRowProps = {
  roles: string[];
  user: UserAr;
  isCreateRow: boolean;
  validateUser: PropValidator<UserAr>;
  onCreateUser: (user: UserAr) => void;
  onUpdateUser: (user: UserAr, isUpdatingPassword: boolean) => void;
  onBlockUser: (userId: string) => void;
  onUnblockUser: (userId: string) => void;
  onDeleteUser: (user: UserAr) => void;
  userResorts: Resort[];
  allResorts: Resort[];
}

export const UserRow = (props: UserRowProps) => {

  const [userName, setUserName] = useState<string>(props.user.userName || '');
  const [password, setPassword] = useState<string>('');
  const [firstName, setFirstName] = useState<string>(props.user.firstName);
  const [lastName, setLastName] = useState<string>(props.user.lastName);
  const [role, setRole] = useState<string>(props.user.role);
  const [email, setEmail] = useState<string>(props.user.email);
  const [resetPassword, setResetPassword] = useState<boolean>(props.user.resetPassword);
  const [resortLocations, setResortLocations] = useState<number[]>(props.user.resortLocations);
  const [isEditing, setIsEditing] = useState<boolean>(props.isCreateRow);
  const [isSettingPassword, setIsSettingPassword] = useState<boolean>(props.isCreateRow);
  const [errors, setErrors] = useState<PropValidationMsg[]>([]);
  const [suggestedUsername, setSuggestedUsername] = useState<string>('');
  const [userNameAvailable, setUserNameAvailable] = useState<boolean>(!!props.user.userName);
  const [emailAvailable, setEmailAvailable] = useState<boolean>(!!props.user.email);
  const [isOpenUserResorts, setIsOpenUserResorts] = useThrottleState(false);

  const token = useSelector( (state: IAppState) => state.auth.bearerToken);
  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);
  const loggedInUser = useSelector((state: IAppState) => state.userState);

  // Users who have never signed in come back with no last login, which parses to the unix epoch
  // and would otherwise display as 12/31/1969 in local time.  Show nothing instead.
  const lastLoginDisplay = useMemo(() => {
    if (isNilOrEmpty(props.user.lastLogin)) {
      return '';
    }
    const lastLoginDate = convertUtcStringDateToDate(props.user, 'lastLogin');

    return lastLoginDate.getTime() <= UNIX_EPOCH.getTime() ? '' : (formatDate(lastLoginDate, dateFormat) ?? '');
  }, [props.user, dateFormat]);

  const requestHeaders = useMemo(() => ({
    method: 'GET',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
    }
  }), [token]);

  const debouncedLastName = useDebounce(lastName, 500);
  const debouncedUserName = useDebounce(userName, 500);
  const debouncedEmail = useDebounce(email, 500);
  const originalEmailRef = React.useRef<string | null>(null)

  const defaultUser = useCallback(() => {
    setUserName(props.user.userName);
    setFirstName(props.user.firstName);
    setLastName(props.user.lastName);
    setRole(props.user.role);
    setEmail(props.user.hasEmail ? props.user.email : '');
    setResetPassword(props.user.resetPassword);
    setResortLocations(props.user.resortLocations);
  }, [props.user]);

  useEffect(() => {
    defaultUser();
  }, [props.user, defaultUser]);

  useEffect(() => {
    originalEmailRef.current = props.user?.email ?? null;
  }, [props.user]);

  /**
   * We are breaking the app wide pattern of async requests through redux because we need to check the username synchronously
   */
  useEffect(() => {
    let sanitizedFirstName = removeBlacklistedCharacters(firstName);
    let sanitizedLastName = removeBlacklistedCharacters(debouncedLastName);
    if (sanitizedFirstName.length > 0 && sanitizedLastName.length > 0 && props.isCreateRow) {
      fetch(apiGetSuggestedUsername(removeBlacklistedCharacters(firstName), removeBlacklistedCharacters(debouncedLastName)), requestHeaders)
        .then(res => res.text())
        .then(res => { setSuggestedUsername(res.substring(0, MAX_USERNAME_LENGTH)); })
        .catch(err => console.log(err));
    }
    if (sanitizedFirstName.length < 1 || sanitizedLastName.length < 1) {
      setSuggestedUsername('');
    }
  }, [debouncedLastName, firstName, requestHeaders, props.isCreateRow]);


  useEffect(() => {
    if (props.isCreateRow && debouncedUserName.length > 0) {
      fetch(checkUserNameAvailable(debouncedUserName), requestHeaders)
        .then(res => res.json())
        .then(res => {
          if (!res) {
            setUserNameAvailable(false)
            return setErrors(prevErrors => [...prevErrors, {propName: 'userName', msg: getResource(resources, 'USER_NAME_NOT_AVAILABLE')}]);
          }
          setUserNameAvailable(true);
          setErrors(prevErrors => prevErrors.filter(error => error.propName !== 'userName'));
        })
        .catch(err => console.log(err));
    }
  }, [debouncedUserName, props.isCreateRow, requestHeaders, resources]);


  useEffect(() => {
    const shouldCheckEmail =
      (props.isCreateRow || isEditing) &&
        debouncedEmail &&
        RegExp(EMAIL_REGEX).test(debouncedEmail) &&
        debouncedEmail !== (originalEmailRef.current ?? "");

    if (shouldCheckEmail) {
      fetch(checkEmailAvailable(debouncedEmail), requestHeaders)
        .then(res => res.json())
        .then(res => {
          if (!res) {
            setEmailAvailable(false)
            return setErrors(prevErrors => [...prevErrors, {propName: 'email', msg: getResource(resources, 'USER_EMAIL_NOT_AVAILABLE')}]);
          }
          setEmailAvailable(true);
          setErrors(prevErrors => prevErrors.filter(error => error.propName !== 'email'));
        })
        .catch(err => console.log(err));
    }
  }, [debouncedEmail, isEditing, props.isCreateRow, requestHeaders, resources]);


  // Reset if user changes 
  useEffect(() => {
    defaultUser();
    setIsSettingPassword(props.isCreateRow);
    setIsEditing(props.isCreateRow);
    setSuggestedUsername('');
    setErrors([]);
  }, [props.isCreateRow, defaultUser]);


  const getError = (prop): string | undefined => {
    const error = find(error => error.propName === prop, errors);
    return path(['msg'], error);
  };

  const upsertUser = (user: UserAr) => {
    const isUpdating = CRUD_STATE.UPDATING === crudStateFor(props.isCreateRow, isEditing);

    // If nothing changed, don't bother with updating (only for update -- create needs to validate all empty state)

    if (isUpdating && checkIfUserChanged(props.user, user)) {
      defaultUser();
      setIsEditing(false);
      setIsSettingPassword(false);
      setErrors([]);
      return;
    }

    // Update
    if (!props.isCreateRow) {
      props.onUpdateUser(user, isSettingPassword);
      return setIsEditing(false);
    }

    props.onCreateUser(user);
    setResortLocations([])
    setPassword('');
    return defaultUser();
  }

  const onDelete = () => props.onDeleteUser({
    userName: userName,
    password: password,
    firstName: firstName,
    lastName: lastName,
    role: role,
    email: email,
    resetPassword: resetPassword,
    resortLocations: resortLocations,
    userId: props.user.userId,
    hasEmail: email.length > 0,
    blocked: props.user.blocked,
    lastLogin: props.user.lastLogin,
  });

  const onSave = () => {
    const userUpdate: UserAr = {
      userName: userName ? userName.trim() : '',
      password: password ? password.trim() : '',
      firstName: firstName ?  firstName.trim() : '',
      lastName: lastName ? lastName.trim() : '',
      role: role.trim(),
      email: email.trim(),
      resetPassword: resetPassword,
      resortLocations: resortLocations,
      userId: props.user.userId,
      hasEmail: email.length > 0,
      blocked: props.user.blocked,
      lastLogin: props.user.lastLogin,
    };

    const errors = props.validateUser(
      userUpdate, {
        validatePassword: isSettingPassword || props.isCreateRow,
        userNameAvailable,
        emailAvailable,
        resources
      });

    setErrors(errors);
    if (errors.length > 0) {
      console.log(`Errors: ${JSON.stringify(errors)}`)
      return;
    }

    upsertUser(userUpdate);
  }

  const handleResetPassword = () => {
    const userUpdate: UserAr = {
      userName: userName ? userName.trim() : '',
      password: password ? password.trim() : '',
      firstName: firstName ?  firstName.trim() : '',
      lastName: lastName ? lastName.trim() : '',
      role: role.trim(),
      email: email.trim(),
      resetPassword: !resetPassword,
      resortLocations: resortLocations,
      userId: props.user.userId,
      hasEmail: email.length > 0,
      blocked: props.user.blocked,
      lastLogin: props.user.lastLogin,
    };
    upsertUser(userUpdate);
  }

  const onCancel = () => {
    defaultUser()
    setIsEditing(false);
    setIsSettingPassword(false);
    setErrors([]);
  }

  const onEdit = () => {
    setIsEditing(true);
    defaultUser()
  }

  const onBlock = () => {
    props.onBlockUser(props.user.userId);
  }

  const onUnblock = () => {
    props.onUnblockUser(props.user.userId);
  }


  const onPasswordChange = (newPassword: string) => {
    setPassword(newPassword);
  }

  const onHandleResortSelect = (options) => {
    if (options.length && options.some(option => option.value === "all")) {
      setResortLocations(resortSelectorOptions.slice(1).map(option => parseInt(option.value)))
    }
    else {
      setResortLocations(options.length ? options.map(option => parseInt(option.value)) : []);
    }
  }

  const onRowBlur = () => setErrors([]);

  const makeEditableCell = (propName: string, children: React.ReactNode) => (
    <td>
      <TooltipView
        side='bottom'
        showTooltip={!!getError(propName)}
        floatingContentCss={css`width: 100%;`}
        tooltipContent={<span css={styles.error}>{getError(propName)}</span>}
        activateOnHover={false}
        onClickInside={doNothing}
        onClickOutside={doNothing}
        onHoverInside={doNothing}
        onHoverOutside={doNothing}
      >
        {children}
      </TooltipView>
    </td>
  );

  const crudState = crudStateFor(props.isCreateRow, isEditing);
  const resortSelectorOptions = loggedInUser.role === "sysadmin" ? generateResortOptions(props.allResorts.filter(option => option.resortId !== 0)) : generateResortOptions(props.userResorts);
  resortSelectorOptions.push({value: "all", label: getResource(resources, "USER_ROW_SELECT_ALL")});
  resortSelectorOptions.sort((a, b) => {
    if (a.label === getResource(resources, "USER_ROW_SELECT_ALL")) return -1;
    if (b.label === getResource(resources, "USER_ROW_SELECT_ALL")) return 1;
    return a.label.localeCompare(b.label);
  });

  return (
    <tr key={props.user.userId} css={props.user.blocked ? css`background-color: ${YELLOW} !important;` : styles.row} onBlur={onRowBlur}>
      { // Username
        isEditing || props.isCreateRow
          ? makeEditableCell(
            'firstName',
            <Input
              type='text'
              maxLength={MAX_FIRST_NAME_LENGTH}
              title={getResource(resources, 'USER_ROW_FIRST_NAME')}
              property='firstName' placeholder={getResource(resources, 'USER_ROW_FIRST_NAME')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              css={!!getError('firstName') ? styles.errorBorder : ''}
            />,
          )
          : <td>{firstName}</td>
      }
      { // Email
        props.isCreateRow || isEditing
          ? makeEditableCell(
            'lastName',
            <Input
              type='text'
              maxLength={MAX_LAST_NAME_LENGTH}
              title={getResource(resources, 'USER_ROW_LAST_NAME')}
              property='lastName'
              placeholder={getResource(resources, 'USER_ROW_LAST_NAME')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              css={!!getError('lastName') ? styles.errorBorder : ''}
            />,
          )
          : <td>{lastName}</td>
      }

      { // Middle Cells
        isEditing || props.isCreateRow
          ? <React.Fragment>
            {
              !props.isCreateRow
                  ? <td>{userName}</td>
                  : makeEditableCell(
                      'userName',
                      <TooltipFC
                          content={<span>{suggestedUsername.length > 0 ? getResource(resources, 'USER_ROW_SUGGESTED_USERNAME', suggestedUsername) : ''}</span>}
                          children={
                            <Input
                                type='text'
                                title={getResource(resources, 'USER_ROW_USERNAME')}
                                maxLength={MAX_USERNAME_LENGTH}
                                property='userName'
                                placeholder={getResource(resources, 'USER_ROW_USERNAME')}
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                                css={!!getError('userName') ? styles.errorBorder : ''}
                            />}
                          delay={10000}
                      />,
                  )
            }
            {
              makeEditableCell(
                'email',
                <Input
                  type='text'
                  title={getResource(resources, 'USER_ROW_EMAIL')}
                  property='email'
                  placeholder={getResource(resources, 'USER_ROW_EMAIL')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  css={!!getError('email') ? styles.errorBorder : css`word-break: break-all;`}
                />,
              )
            }
            {
              makeEditableCell(
                'role',
                <Dropdown
                  resources={resources}
                  items={props.roles}
                  selectedItem={role}
                  noItemsMessage={getResource(resources, 'USER_ROW_NO_ROLES')}
                  placeholderMessage={getResource(resources, 'USER_ROW_USER_ROLE')}
                  onChange={(e) => setRole(e)}
                  rootCss={css`
                    ${styles.rowItemHeight};
                    ${!!getError('role') ? styles.errorBorder : ''}
                  `}
                />,
              )
            }
            {
              makeEditableCell(
                'resortLocations',
                <div css={!!getError('resortLocation') ? styles.errorBorder : ''}>
                  <Select
                    isMulti={true}
                    name={'resortLocations'}
                    options={resortSelectorOptions}
                    placeholder={getResource(resources, 'USER_ROW_SELECT_PLACEHOLDER')}
                    value={generateOptionsFromIds(resortLocations, props.allResorts)}
                    onChange={onHandleResortSelect}
                  />
                </div>,
              )
            }
            {
              makeEditableCell(
                'password',
                <Switcher selectedIndex={isSettingPassword ? 1 : 0}>
                  {/* Reset Password Button */}
                  <BigNeutral
                    onClick={ () => setIsSettingPassword(true)}
                    css={css`styles.passwordButton`}
                  >{getResource(resources, 'USER_ROW_UPDATE_PASSWORD')}</BigNeutral>

                  {/* Password Field */}
                  <PasswordField
                    resources={resources}
                    onPasswordChange={onPasswordChange}
                    password={password}
                    editable={isEditing}
                    placeholder={getResource(resources, 'USER_ROW_PASSWORD')}
                    autofill={false}
                    rootCss={!!getError('password') ? styles.errorBorder : css``}
                  />,
                </Switcher>,
              )
            }
          </React.Fragment>
          : <React.Fragment>
            <td>{userName}</td>
            <td css={styles.email} >{email}</td>
            <td>{role}</td>
            <td>
              <div css={styles.userResortsOverview}>
                <button css={styles.userResortsExpandButton} 
                        onClick={() => setIsOpenUserResorts(!isOpenUserResorts)}>
                  <img src={isOpenUserResorts ? collapse : expand}
                      alt="Open"
                      title={getResource(resources, isOpenUserResorts ? 'USER_ROW_COLLAPSE_RESORTS' : 'USER_ROW_EXPAND_RESORTS')}
                      css={styles.expandButton}/>
                </button>
                { 
                  isOpenUserResorts ? 
                  <div css={styles.userResortsListContainer}>
                    {
                      mapIdsToResorts(resortLocations, props.allResorts).map((resort: Resort, index: number, array) => {
                        return( 
                          <div css={css`color: ${DARK_GREY}; padding: 4px;`}>
                            {resort.resortName}
                            {index < array.length-1 ? ', ' : ''}
                          </div>);
                      })
                    }
                  </div> :
                  <span css={userResortsAbbreviations}>
                    {
                      hasAllResorts(resortLocations, props.allResorts) ? getResource(resources, 'USER_ROW_ALL_RESORTS') : mapIdsToResortAbbrevs(resortLocations, props.allResorts)
                    }
                  </span>
                } 
              </div>
            </td>
            {/* Password Dots */}
            <td css={css`font-size: 3.2rem !important;`}>
              &#183;&#183;&#183;&#183;&#183;&#183;
            </td>
          </React.Fragment>
      }
      <td>{lastLoginDisplay}</td>
      <td>
        <CrudButtons
          currentState={crudState}
          createButtonTitle={crudState === CRUD_STATE.CREATING ? getResource(resources, 'USER_ROW_CREATE_USER') : getResource(resources, 'USER_ROW_SAVE_CHANGES')}
          readButtonTitle={getResource(resources, 'USER_ROW_DISCARD_CHANGES')}
          updateButtonTitle={getResource(resources, 'USER_ROW_UPDATE_USER')}
          deleteButtonTitle={getResource(resources, 'USER_ROW_DELETE_USER')}
          onCreate={onSave}
          onRead={onCancel}
          onUpdate={onEdit}
          onDelete={onDelete}
          onBlock={onBlock}
          onUnblock={onUnblock}
          rootCss={styles.rowItemHeight}
          blocked={props.user.blocked}
          handleResetPassword={handleResetPassword}
          resetPassword={resetPassword}
        />
      </td>
    </tr>
  )
}
